import React, {useEffect, useState} from "react";
import * as Yup from "yup";
import {makeRequired, makeValidate} from "mui-rff";
import classnames from "classnames";
import Form from "../../../../base/form";
import {Fade, SvgIcon, Tooltip} from "@mui/material";
import {OutlinedInputForm} from "../../../../base/input/outlined_input";
import IconButton from "../../../../base/button/icon_button";
import {Edit} from "@mui/icons-material";
import {ReactComponent as RemoveIcon} from "../../../../../../assets/images/remove.svg";
import {OutlinedDropdownForm} from "../../../../base/dropdown/outlined_dropdown";
import {Checkbox, CheckboxForm} from "../../../../base/checkbox/checkbox";
import SaveOrRollback from "../../../save-or-rollback";
import {deepEqual} from "../../../../../../core/services/utils/utils";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {ReactComponent as Icon} from "../../../../../../assets/images/part-support-request/checkbox.svg";
import {ReactComponent as IconCheckbox} from "../../../../../../assets/images/part-support-request/checkbox-active.svg";
import ValidateMessages from "../../../../../../core/models/static/texts/vallidate-messages";


const schema = Yup.object().shape({
    title: Yup.string().trim().nullable().required(ValidateMessages.required),
    fileType: Yup.object().nullable().required(),
    isRequired: Yup.boolean().nullable().required().default(false),
})


const WarrantyAndReturnTemplateCard = ({template, fileTypes, update, remove}) => {
    const [editing, setEditing] = useState(false)
    const [initialValues, setInitialValues] = useState({})
    const validate = makeValidate(schema)
    const required = makeRequired(schema)
    const isMounted = useIsMounted()

    /**
     * Listens to the changes in the template and with each change:
     * sets the initialValues of the form.
     */
    useEffect(() => {
        setInitialValues({
            title: template?.title ?? '',
            isRequired: template?.isRequired ?? false,
            fileType: template.validFileType,
        })
    }, [template])

    /**
     * Reverts the values of this template.
     * @param {any} values
     * @param {formApi} form
     */
    const revertValues = (values, form) => {
        setEditing(false)
        form.reset(initialValues)
    }

    /**
     * Invokes the update callback with the updated template information.
     */
    const updateTemplate = async (values) => {
        if (!values.title?.length || !values.fileType) {
            setEditing(false)
            return;
        }
        if (deepEqual(values, initialValues)) {
            setEditing(false)
            return;
        }
        const resultFlag = await update({
            ...template,
            title: values.title,
            isRequired: values.isRequired,
            validFileType: values.fileType,
        })
        if (resultFlag && isMounted()) {
            setEditing(false)
        }
    }

    /**
     * Opens to delete confirmation modal to ask for users decision about removing this template.
     * If the user confirms, then removes the template.
     */
    const getUserConfirmation = () => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    Do you want to delete this template?
                </p>
            </div>,
            {},
            (decision) => decision && remove(template)
        );
    }

    return (
        <>
            <div className={classnames('template-card', {'editing': editing})}>
                <Form
                    className={''}
                    onSubmit={() => false}
                    validate={validate}
                    initialValues={initialValues}
                    render={({values, form}) => {
                        return (
                            <>
                                <div className={'d-flex flex-column'}>
                                    <Fade
                                        in={!editing}
                                        mountOnEnter
                                        unmountOnExit
                                        className={'d-flex justify-content-end align-items-center'}>
                                        <div>
                                            <IconButton
                                                disabled={template.editing || editing}
                                                className={"mx-1 ml-2"}
                                                color={'paper.secondary'}
                                                onPressed={() => setEditing(true)}
                                            >
                                                <Edit className={"icon normal"}/>
                                            </IconButton>
                                            <IconButton
                                                onPressed={() => getUserConfirmation()}
                                                disabled={template?.editing || editing}
                                                color={'paper.error'}
                                            >
                                                <SvgIcon component={RemoveIcon} className='icon normal'/>
                                            </IconButton>
                                        </div>
                                    </Fade>
                                    <div className={classnames('fade-container', {
                                        'expanded': editing,
                                    })}>
                                        <Fade in={editing} className={'d-flex'}>
                                            <div>
                                                <OutlinedInputForm
                                                    name={'title'}
                                                    placeholder={'title of the template'}
                                                    label={"Title"}
                                                    multiline
                                                    className={'mr-2 max-w-80'}
                                                    required={required.title}
                                                    value={values.title ?? ''}
                                                    onChange={(e) => form.change(e.target.name, e.target.value)}
                                                />
                                            </div>
                                        </Fade>
                                        <Fade in={!editing} className={'d-flex'}>
                                            <div>
                                                <p className={'title'}>
                                                    {template?.title ?? ''}
                                                </p>
                                            </div>
                                        </Fade>
                                    </div>
                                    <div className={classnames('fade-container', {
                                        'expanded': editing,
                                    })}>
                                        <Fade in={editing}
                                              className={'d-flex justify-content-between align-items-center'}>
                                            <div>
                                                <div className={'d-flex align-items-center flex-grow-1'}>
                                                    <span className={'text-sm font-weight-semi-bold black mr-3'}>
                                                        Accepted Formats
                                                    </span>
                                                    <OutlinedDropdownForm
                                                        fullWidth={false}
                                                        name={'fileType'}
                                                        required={required.fileType}

                                                        data={fileTypes.map(e => ({
                                                            value: e,
                                                            label: e.title,
                                                            id: e.id,
                                                        })) ?? []}
                                                    />
                                                </div>
                                                <div className={'ignore-background'}>
                                                    <CheckboxForm
                                                        name={'isRequired'}
                                                        required={required.isRequired}
                                                        data={{
                                                            value: values.isRequired,
                                                            label: 'Required to fill'
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </Fade>
                                        <Fade in={!editing} className={'d-flex justify-content-between checkbox-container'}>
                                            <div>
                                                <div className={'d-flex align-items-center'}>
                                                    <p className={'text-sm black'}>
                                                        Accepted Formats
                                                    </p>
                                                    <p className={'font-weight-bold text-sm ml-4'}>
                                                        {template.validFileType?.title ?? 'Unknown'}
                                                    </p>
                                                </div>
                                                <Tooltip
                                                    disableFocusListener={editing}
                                                    disableHoverListener={editing}
                                                    disableTouchListener={editing}
                                                    aria-disabled={editing}
                                                    title={"Read Only"}
                                                    className={'cursor-not-allowed-hover'}
                                                    disableInteractive
                                                >
                                                    <span>
                                                        <Checkbox
                                                            className={'cursor-not-allowed-hover'}
                                                            labelClassName={'cursor-not-allowed-hover'}
                                                            value={template.isRequired ?? false}
                                                            label={'Required to fill'}
                                                            checked={template.isRequired ?? false}
                                                            onChange={() => false}
                                                            icon={<Icon/>}
                                                            checkedIcon={<IconCheckbox/>}
                                                        />
                                                    </span>
                                                </Tooltip>
                                            </div>
                                        </Fade>
                                    </div>
                                    <Fade in={editing}
                                          mountOnEnter
                                          unmountOnExit
                                          className={'d-flex justify-content-end align-items-center'}>
                                        <div>
                                            <SaveOrRollback
                                                column={false}
                                                loading={template.editing}
                                                show={values['title']?.trim().length > 0}
                                                onRollback={() => revertValues(values, form)}
                                                onSave={() => updateTemplate(values)}
                                            />
                                        </div>
                                    </Fade>
                                </div>
                            </>
                        );
                    }}
                />
            </div>
        </>
    );
}

export default WarrantyAndReturnTemplateCard;
